/**
 * Terms Content
 *
 * Public terms page content rendered inside the shared legal shell.
 *
 * Primary responsibilities:
 * - Transform translated terms content into structured sections
 * - Keep the terms page aligned with the redesigned marketing shell
 *
 * Dependencies:
 * - legal-page-shell
 * - i18n
 */

'use client'

import { useTranslation } from 'react-i18next'

import '@/i18n'
import { LegalPageShell } from '@/components/marketing/legal-page-shell'
import { readTranslatedItems, readTranslatedText } from '@/components/marketing/legal-translation'

const fallbackTermsCopy = {
  backToHome: 'Back to Home',
  pageTitle: 'Terms and Conditions',
  sections: {
    acceptance: {
      title: '1. Acceptance of Terms',
      text:
        'By accessing and using Losi, you agree to be bound by these Terms and Conditions. If you do not agree with any part of these terms, you may not use our service.'
    },
    serviceUse: {
      title: '2. Use of Service',
      intro:
        'You agree to use the Service only for lawful purposes and in accordance with these Terms. You are prohibited from:',
      items: [
        'Using the service for any illegal activities',
        'Attempting to bypass any security measures',
        'Sharing your account credentials with others',
        'Using the service to harm others or spread misinformation'
      ]
    },
    aiUsage: {
      title: '3. AI Model Usage',
      intro: 'Our service provides access to various AI models. You agree to:',
      items: [
        'Use AI models responsibly and ethically',
        'Respect the usage limits specified in your plan',
        'Not attempt to extract or reverse engineer AI models',
        'Take responsibility for content you generate using our service'
      ]
    },
    privacy: {
      title: '4. Privacy and Data',
      intro:
        'We prioritize your privacy and data security. Your use of the Service is also governed by our Privacy Policy. Key points include:',
      items: [
        'Your conversations remain private and are not used to train AI models',
        'Generated content, including images, belongs to you',
        'We employ enterprise-grade security measures',
        'We maintain transparency in our data handling practices'
      ]
    },
    availability: {
      title: '5. Service Availability',
      text:
        'While we strive for maximum uptime, we do not guarantee uninterrupted access to the Service. We reserve the right to modify, suspend, or discontinue any part of the Service without notice.'
    },
    changes: {
      title: '6. Changes to Terms',
      text:
        'We may update these Terms from time to time. We will notify you of significant changes and your continued use of the Service constitutes acceptance of the modified Terms.'
    },
    contact: {
      title: '7. Contact',
      text: 'If you have any questions about these Terms, please contact us at support@marshmallow.studio'
    }
  }
} as const

export function TermsContent() {
  const { t } = useTranslation()

  const serviceUseItems = readTranslatedItems(
    t('terms.sections.service_use.items', {
      returnObjects: true,
      defaultValue: fallbackTermsCopy.sections.serviceUse.items
    }),
    fallbackTermsCopy.sections.serviceUse.items
  )
  const aiUsageItems = readTranslatedItems(
    t('terms.sections.ai_usage.items', {
      returnObjects: true,
      defaultValue: fallbackTermsCopy.sections.aiUsage.items
    }),
    fallbackTermsCopy.sections.aiUsage.items
  )
  const privacyItems = readTranslatedItems(
    t('terms.sections.privacy.items', {
      returnObjects: true,
      defaultValue: fallbackTermsCopy.sections.privacy.items
    }),
    fallbackTermsCopy.sections.privacy.items
  )

  return (
    <LegalPageShell
      eyebrow="Terms"
      title={readTranslatedText(
        t('terms.page_title', { defaultValue: fallbackTermsCopy.pageTitle }),
        fallbackTermsCopy.pageTitle
      )}
      description={readTranslatedText(
        t('terms.sections.acceptance.text', {
          defaultValue: fallbackTermsCopy.sections.acceptance.text
        }),
        fallbackTermsCopy.sections.acceptance.text
      )}
      backLabel={readTranslatedText(
        t('terms.back_to_home', { defaultValue: fallbackTermsCopy.backToHome }),
        fallbackTermsCopy.backToHome
      )}
      lastUpdated="October 29, 2026"
      sections={[
        {
          title: readTranslatedText(
            t('terms.sections.acceptance.title', {
              defaultValue: fallbackTermsCopy.sections.acceptance.title
            }),
            fallbackTermsCopy.sections.acceptance.title
          ),
          body: readTranslatedText(
            t('terms.sections.acceptance.text', {
              defaultValue: fallbackTermsCopy.sections.acceptance.text
            }),
            fallbackTermsCopy.sections.acceptance.text
          )
        },
        {
          title: readTranslatedText(
            t('terms.sections.service_use.title', {
              defaultValue: fallbackTermsCopy.sections.serviceUse.title
            }),
            fallbackTermsCopy.sections.serviceUse.title
          ),
          intro: readTranslatedText(
            t('terms.sections.service_use.intro', {
              defaultValue: fallbackTermsCopy.sections.serviceUse.intro
            }),
            fallbackTermsCopy.sections.serviceUse.intro
          ),
          items: serviceUseItems
        },
        {
          title: readTranslatedText(
            t('terms.sections.ai_usage.title', {
              defaultValue: fallbackTermsCopy.sections.aiUsage.title
            }),
            fallbackTermsCopy.sections.aiUsage.title
          ),
          intro: readTranslatedText(
            t('terms.sections.ai_usage.intro', {
              defaultValue: fallbackTermsCopy.sections.aiUsage.intro
            }),
            fallbackTermsCopy.sections.aiUsage.intro
          ),
          items: aiUsageItems
        },
        {
          title: readTranslatedText(
            t('terms.sections.privacy.title', {
              defaultValue: fallbackTermsCopy.sections.privacy.title
            }),
            fallbackTermsCopy.sections.privacy.title
          ),
          intro: readTranslatedText(
            t('terms.sections.privacy.intro', {
              defaultValue: fallbackTermsCopy.sections.privacy.intro
            }),
            fallbackTermsCopy.sections.privacy.intro
          ),
          items: privacyItems
        },
        {
          title: readTranslatedText(
            t('terms.sections.availability.title', {
              defaultValue: fallbackTermsCopy.sections.availability.title
            }),
            fallbackTermsCopy.sections.availability.title
          ),
          body: readTranslatedText(
            t('terms.sections.availability.text', {
              defaultValue: fallbackTermsCopy.sections.availability.text
            }),
            fallbackTermsCopy.sections.availability.text
          )
        },
        {
          title: readTranslatedText(
            t('terms.sections.changes.title', {
              defaultValue: fallbackTermsCopy.sections.changes.title
            }),
            fallbackTermsCopy.sections.changes.title
          ),
          body: readTranslatedText(
            t('terms.sections.changes.text', {
              defaultValue: fallbackTermsCopy.sections.changes.text
            }),
            fallbackTermsCopy.sections.changes.text
          )
        },
        {
          title: readTranslatedText(
            t('terms.sections.contact.title', {
              defaultValue: fallbackTermsCopy.sections.contact.title
            }),
            fallbackTermsCopy.sections.contact.title
          ),
          body: readTranslatedText(
            t('terms.sections.contact.text', {
              defaultValue: fallbackTermsCopy.sections.contact.text
            }),
            fallbackTermsCopy.sections.contact.text
          )
        }
      ]}
    />
  )
}
